import React, { useState, useEffect } from "react";
import Sidebar from "../../../Sidebar/Sidebar";
import Header from "../../../Header/Header";
import { FaTrash, FaEdit } from "react-icons/fa";
import AddCommitteeMemberModal from "./Components/AddCommitteeMemberModal";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Cookies from "js-cookie";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HiOutlineSearch } from "react-icons/hi";

const Committee = () => {
  // Sidebar state and toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ALL ORIGINAL STATE
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;
  const authToken = Cookies.get("authToken");

  // Filter members based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredMembers(committeeMembers);
    } else {
      const filtered = committeeMembers.filter(member => 
        member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.designation?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMembers(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, committeeMembers]);

  useEffect(() => {
    const fetchCommitteeMembers = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllExecutiveCommittee`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !Array.isArray(data.data)) {
          throw new Error("Invalid data format received from server");
        }

        setCommitteeMembers(data.data);
        setFilteredMembers(data.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching committee members:", err);
        setError("Failed to load committee members: " + err.message);
        setCommitteeMembers([]);
        setFilteredMembers([]);
        toast.error("Failed to load committee members");
      } finally {
        setLoading(false);
      }
    };

    fetchCommitteeMembers();
  }, [authToken]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const currentData = filteredMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

  const handleMemberAdded = (newMember) => {
    setCommitteeMembers((prev) => [newMember, ...prev]);
    toast.success("Committee member added successfully");
  };

  const openDeleteModal = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
    setIsDeleting(false);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    try {
      setIsDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/deleteExecutiveCommittee/${memberToDelete._id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to delete member");
      }

      setCommitteeMembers((prev) =>
        prev.filter((member) => member._id !== memberToDelete._id)
      );
      toast.success("Committee member deleted successfully");
      closeDeleteModal();
    } catch (err) {
      console.error("Error deleting committee member:", err);
      toast.error(err.message || "Failed to delete committee member");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const handleMemberUpdated = (updatedMember) => {
    setCommitteeMembers((prev) =>
      prev.map((member) =>
        member._id === updatedMember._id ? updatedMember : member
      )
    );
    toast.success("Committee member updated successfully");
  };

  const renderSkeletonRows = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <tr key={`skeleton-${index}`} className="even:bg-gray-50">
          <td className="px-6 py-4">
            <div className="flex items-center">
              <Skeleton circle width={40} height={40} className="mr-3" />
              <Skeleton width={150} />
            </div>
          </td>
          <td className="px-6 py-4">
            <Skeleton width={120} />
          </td>
          <td className="px-6 py-4">
            <Skeleton width={200} />
          </td>
          <td className="px-6 py-4">
            <div className="flex space-x-3">
              <Skeleton circle width={20} height={20} />
              <Skeleton circle width={20} height={20} />
            </div>
          </td>
        </tr>
      ));
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
      {/* Pass sidebar props */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/committee" />
      
      <div className="flex-1 w-0">
        {/* Pass toggleSidebar to Header */}
        <Header toggleSidebar={toggleSidebar} />
        
        <main className="p-4 lg:p-6 overflow-auto">
          <div className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200">
            {/* Header with Search and Add Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800">Executive Committee</h2>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                {/* Search Bar */}
                <div className="relative w-full sm:w-64">
                  <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
                  />
                </div>
                
                <button
                  className="w-full sm:w-auto bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors"
                  onClick={() => {
                    setEditingMember(null);
                    setIsModalOpen(true);
                  }}
                >
                  <span>+ Add Member</span>
                </button>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="block lg:hidden space-y-3">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <Skeleton circle width={50} height={50} />
                      <div className="flex-1">
                        <Skeleton width="80%" height={18} />
                        <Skeleton width="60%" height={14} className="mt-2" />
                      </div>
                    </div>
                  </div>
                ))
              ) : currentData.length > 0 ? (
                currentData.map((member) => (
                  <div key={member._id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="h-12 w-12 rounded-full object-cover border border-gray-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/48";
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800">{member.name}</h3>
                        <p className="text-sm text-gray-600">{member.designation}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end space-x-3 mt-3 pt-3 border-t border-gray-200">
                      <FaEdit
                        className="text-gray-600 hover:text-gray-800 cursor-pointer transition-colors"
                        title="Edit"
                        onClick={() => handleEditMember(member)}
                        size={18}
                      />
                      <FaTrash
                        className="text-gray-500 hover:text-red-500 cursor-pointer transition-colors"
                        title="Delete"
                        onClick={() => openDeleteModal(member)}
                        size={18}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? `No members found matching "${searchTerm}"` : "No committee members found"}
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    renderSkeletonRows()
                  ) : currentData.length > 0 ? (
                    currentData.map((member) => (
                      <tr key={member._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <img
                              src={member.image}
                              alt={member.name}
                              className="h-10 w-10 rounded-full object-cover mr-3 border border-gray-200"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/40";
                              }}
                            />
                            <div className="text-sm font-medium text-gray-800">
                              {member.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-700">
                            {member.designation}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">
                            {member.name}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <FaEdit
                              className="text-gray-600 hover:text-gray-800 cursor-pointer transition-colors"
                              title="Edit"
                              onClick={() => handleEditMember(member)}
                              size={18}
                            />
                            <FaTrash
                              className="text-gray-500 hover:text-red-500 cursor-pointer transition-colors"
                              title="Delete"
                              onClick={() => openDeleteModal(member)}
                              size={18}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                        {searchTerm ? `No members found matching "${searchTerm}"` : "No committee members found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredMembers.length > itemsPerPage && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <div className="flex items-center space-x-2">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => handlePageChange(i + 1)}
                      className={`w-8 h-8 text-sm rounded-md transition-colors ${
                        currentPage === i + 1
                          ? "bg-gray-700 text-white"
                          : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add/Edit Member Modal - YOUR ORIGINAL (needs separate update) */}
      <AddCommitteeMemberModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingMember(null);
        }}
        onMemberAdded={handleMemberAdded}
        onMemberUpdated={handleMemberUpdated}
        memberToEdit={editingMember}
      />

      {/* Delete Confirmation Modal - Updated with gray theme */}
      {showDeleteModal && memberToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg lg:text-xl font-bold text-gray-800">Confirm Deletion</h3>
              <button
                onClick={closeDeleteModal}
                className="text-gray-500 hover:text-gray-700 p-1"
                disabled={isDeleting}
              >
                <svg
                  className="w-5 h-5 lg:w-6 lg:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mb-6">
              <p className="text-sm lg:text-base text-gray-700">
                Are you sure you want to delete the committee member <strong className="text-gray-800">"{memberToDelete.name}"</strong>?
              </p>
              <p className="text-xs lg:text-sm text-gray-500 mt-2">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={closeDeleteModal}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteMember}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center text-sm transition-colors"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Committee;